import React, { useEffect, useState } from 'react'
import { DataGrid } from '@mui/x-data-grid'
import { getFundBalance, manageFundsRequest } from '../../../services/event'
import '../style.scss'
import {
	Typography,
	Modal,
	Button,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from '@mui/material'
import { LocalAtm, CheckCircleRounded, MoneyOff } from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import { getAreas, isMobileScreen } from '../../../utils'
import { CustomDataGrid } from '../../../components/CustomTable/CustomDataGrid'

const PREFIX = 'FundScreen'

const classes = {
	root: `${PREFIX}-root`,
	media: `${PREFIX}-media`,
	modalRoot: `${PREFIX}-modalRoot`,
	modal: `${PREFIX}-modal`,
	paper: `${PREFIX}-paper`,
	reasonInput: `${PREFIX}-reasonInput`,
}

const Root = styled('div')({
	[`& .${classes.root}`]: {
		maxWidth: 345,
	},
	[`& .${classes.media}`]: {
		height: 140,
	},

	[`& .${classes.modalRoot}`]: {
		// height: 300,
		position: 'absolute',
		flexGrow: 1,
		minWidth: 300,
		transform: 'translateZ(0)',
		// The position fixed scoping doesn't work in IE 11.
		// Disable this demo to preserve the others.
		'@media all and (-ms-high-contrast: none)': {
			display: 'none',
		},
	},
	[`& .${classes.modal}`]: {
		display: 'flex',
		// padding: theme.spacing(1),
		alignItems: 'center',
		justifyContent: 'center',
	},
	[`& .${classes.paper}`]: {
		width: 400,
		backgroundColor: 'white',
		border: '2px solid #000',
		borderRadius: '8px',
		// boxShadow: theme.shadows[5],
		padding: '40px',
		lineHeight: '1.44',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
	},

	[`& .${classes.reasonInput}`]: {
		height: '88px',
		overflow: 'hidden',
		width: '100%',
		marginTop: '16px',
		padding: '12px',
	},
})

const FundScreen = () => {
	const [filters, setFilters] = useState({
		areaId: 'ALL',
		page: 0,
		pageSize: 10,
	})
	const [data, setData] = useState([])
	const [loading, setLoading] = useState(false)
	const [fundsModal, setFundsModal] = useState(null)
	const [fundInput, setFundInput] = useState(0)

	useEffect(() => {
		const areaId = filters.areaId === 'ALL' ? undefined : filters.areaId
		setLoading(true)
		getFundBalance(areaId).then((res) => {
			setData(res.data)
			setLoading(false)
		})
	}, [filters.areaId])

	const rowWidthConfig = {
		area: { default: { flex: 0.2 }, mobile: { width: 150 } },
		freeBalance: { default: { flex: 0.2 }, mobile: { width: 150 } },
		holdBalance: { default: { flex: 0.2 } },
		totalBalance: { default: { flex: 0.2 }, mobile: { width: 150 } },
		user: { default: { width: 320 } },
	}

	const addFunds = () => {
		manageFundsRequest({
			amount: fundInput,
			areaId: fundsModal?.id,
			credit: true,
		}).then(() => {
			setData(
				data.map((d) =>
					d._id === fundsModal?.id
						? {
								...d,
								fund: {
									...d.fund,
									totalBalance:
										d.fund.totalBalance + fundInput,
									freeBalance: d.fund.freeBalance + fundInput,
								},
						  }
						: { ...d }
				)
			)
		})
		setFundsModal(null)
		setFundInput(0)
	}

	const removeFunds = () => {
		manageFundsRequest({
			amount: fundInput,
			areaId: fundsModal?.id,
			credit: false,
		}).then(() => {
			setData(
				data.map((d) =>
					d._id === fundsModal?.id
						? {
								...d,
								fund: {
									...d.fund,
									totalBalance:
										d.fund.totalBalance - fundInput,
									freeBalance: d.fund.freeBalance - fundInput,
								},
						  }
						: { ...d }
				)
			)
		})
		setFundsModal(null)
		setFundInput(0)
	}

	const onFilterChange = (name, value) => {
		setFilters((f) => ({
			...f,
			[name]: value,
			page: 1,
		}))
	}

	const onPageSizeChange = ({ page, pageSize }) => {
		setFilters((f) => ({
			...f,
			page,
			pageSize,
		}))
	}

	const headCells = [
		{
			field: 'area',
			numeric: false,
			headerName: 'Area',
			sortable: false,
		},
		{
			field: 'freeBalance',
			numeric: false,
			headerName: 'Free Balance',
			sortable: false,
		},
		{
			field: 'holdBalance',
			numeric: false,
			headerName: 'Hold Balance',
			sortable: false,
		},
		{
			field: 'totalBalance',
			numeric: false,
			headerName: 'Total Balance',
			sortable: true,
		},
		{
			field: 'user',
			headerName: 'Actions',
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<strong>
					<Button
						variant="contained"
						color="primary"
						startIcon={<LocalAtm />}
						onClick={() =>
							setFundsModal({ ...params.row, credit: true })
						}
						aria-label="Add Fund"
					>
						Add Fund
					</Button>
					&nbsp;&nbsp;&nbsp;&nbsp;
					<Button
						variant="outlined"
						color="primary"
						startIcon={<MoneyOff />}
						onClick={() =>
							setFundsModal({ ...params.row, credit: false })
						}
						aria-label="Remove Fund"
					>
						Remove Fund
					</Button>
				</strong>
			),
		},
	]

	return (
		<Root className="screen fund-screen">
			<div className={classes.modalRoot}>
				<Modal
					open={!!fundsModal}
					onClose={() => setFundsModal(null)}
					className={classes.modal}
				>
					<div className={classes.paper}>
						<Typography
							variant="body2"
							style={{
								textAlign: 'center',
								fontWeight: 'bold',
							}}
						>
							Adding funds to
						</Typography>
						<Typography
							variant="body"
							style={{
								marginBottom: '20px',
								textAlign: 'center',
							}}
						>
							{fundsModal?.name}
						</Typography>
						<div className="balance-info">
							<div>
								<Typography
									variant="body2"
									style={{
										textAlign: 'center',
										fontWeight: 'bold',
									}}
								>
									Free Balance
								</Typography>
								<Typography
									variant="body"
									style={{
										marginBottom: '20px',
										textAlign: 'center',
									}}
								>
									₹
									{fundsModal?.credit
										? fundsModal?.fund?.freeBalance +
										  fundInput
										: fundsModal?.fund?.freeBalance -
										  fundInput}
								</Typography>
							</div>
							<div>
								<Typography
									variant="body2"
									style={{
										textAlign: 'center',
										fontWeight: 'bold',
									}}
								>
									Hold Balance
								</Typography>
								<Typography
									variant="body"
									style={{
										marginBottom: '20px',
										textAlign: 'center',
									}}
								>
									₹{fundsModal?.fund?.holdBalance}
								</Typography>
							</div>
							<div>
								<Typography
									variant="body2"
									style={{
										textAlign: 'center',
										fontWeight: 'bold',
									}}
								>
									Total Balance
								</Typography>
								<Typography
									variant="body"
									style={{
										marginBottom: '20px',
										textAlign: 'center',
									}}
								>
									₹
									{fundsModal?.credit
										? fundsModal?.fund?.totalBalance +
										  fundInput
										: fundsModal?.fund?.totalBalance -
										  fundInput}
								</Typography>
							</div>
						</div>
						<TextField
							className="m-top-16"
							label="Fund Amount"
							variant="outlined"
							defaultValue={fundInput}
							value={fundInput}
							type="number"
							onChange={(e) => setFundInput(+e.target.value)}
							fullWidth
						/>
						<div>
							<Button
								className="m-top-16"
								variant="contained"
								color="primary"
								startIcon={<CheckCircleRounded />}
								onClick={
									fundsModal?.credit ? addFunds : removeFunds
								}
								aria-label="Accept"
								style={{ marginRight: '12px' }}
							>
								{fundsModal?.credit ? 'Add' : 'Remove'} ₹
								{fundInput}
							</Button>
						</div>
					</div>
				</Modal>
			</div>
			<div className="filter-header">
				<FormControl variant="outlined">
					<InputLabel id="demo-simple-select-outlined-label">
						Area
					</InputLabel>
					<Select
						className="area-input"
						value={filters.areaId}
						onChange={(e) =>
							onFilterChange(e.target.name, e.target.value)
						}
						label="Area"
						name="areaId"
					>
						<MenuItem value={'ALL'}>All Areas</MenuItem>
						{getAreas().map((area, i) => (
							<MenuItem value={area._id} key={i}>
								{area.name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</div>
			<CustomDataGrid
				rows={data.map((s) => ({
					...s,
					id: s._id,
					area: s.name,
					freeBalance: s.fund.freeBalance,
					holdBalance: s.fund.holdBalance,
					totalBalance: s.fund.totalBalance,
				}))}
				columns={headCells.map((col) => ({
					...col,
					sortable: true,
					disableClickEventBubbling: true,
					// resizable: true,
					...(isMobileScreen()
						? rowWidthConfig[col.field].mobile ||
						  rowWidthConfig[col.field].default
						: rowWidthConfig[col.field].default),
				}))}
				rowCount={data.length}
				loading={loading}
				paginationMode="client"
				initialState={{
					pagination: {
						paginationModel: {
							pageSize: filters.pageSize,
						},
					},
				}}
				paginationModel={filters}
				onPaginationModelChange={(page) =>
					onPageSizeChange({
						pageSize: page.pageSize,
						page: page.pageSize != filters.pageSize ? 0 : page.page,
					})
				}
			/>
		</Root>
	)
}

export default FundScreen
